import { ChangeDetectionStrategy, Component, computed, input, output } from '@angular/core';
import { MatIcon } from '@angular/material/icon';

export interface SegmentedFilterItem {
  id: string | null;
  label: string;
  count?: number;
  /** Material-Icon-Name vor dem Label. */
  icon?: string;
  testId?: string;
}

/**
 * `radio` filtert eine Liste (role=radiogroup), `tabs` wechselt die Sicht einer
 * Seite (role=tablist) — gleiche Bedienung, andere Semantik fuer Screenreader.
 */
export type SegmentedFilterVariant = 'radio' | 'tabs';

/** `compact` traegt die engeren Masse des Umgebungs-Segments (page-testresultate.jsx:122-127). */
export type SegmentedFilterDensity = 'default' | 'compact';

@Component({
  selector: 'app-segmented-filter-control',
  standalone: true,
  imports: [MatIcon],
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './segmented-filter-control.component.html',
  styleUrl: './segmented-filter-control.component.scss',
})
export class SegmentedFilterControlComponent {
  readonly items = input<SegmentedFilterItem[]>([]);
  readonly value = input<string | null>(null);
  readonly variant = input<SegmentedFilterVariant>('radio');
  readonly density = input<SegmentedFilterDensity>('default');
  /** Tab-Variante: Praefix der Button-IDs (`<prefix>-<item.id>`), damit ein Panel sich per `aria-labelledby` benennt. */
  readonly idPrefix = input<string | null>(null);
  /** Tab-Variante: ID des Panels, das die Tabs steuern (`aria-controls`). */
  readonly controls = input<string | null>(null);
  /**
   * Zugaenglicher Name der Radio-Gruppe. Pflicht — eine unbenannte Gruppe ist
   * fuer Screenreader wertlos (fboudouhi Blocker: `role="group"` ohne Name).
   */
  readonly ariaLabel = input.required<string>();

  readonly valueChange = output<string | null>();

  // Ein Item ist aktiv, wenn seine id dem Wert entspricht. Als computed statt
  // Methode, damit die Bindung nicht pro CD-Zyklus neu ausgewertet wird
  // (fboudouhi Kleinigkeit).
  readonly activeId = computed(() => this.value());

  readonly isTabs = computed(() => this.variant() === 'tabs');

  onSelect(id: string | null): void {
    if (id === this.value()) return;
    this.valueChange.emit(id);
  }

  buttonId(item: SegmentedFilterItem): string | null {
    const prefix = this.idPrefix();
    return prefix ? `${prefix}-${item.id}` : null;
  }

  isActive(item: SegmentedFilterItem): boolean {
    return item.id === this.activeId();
  }

  /**
   * WCAG-Standardverhalten fuer role="radiogroup": Pfeiltasten wandern durch
   * die Radios, Home/End springen an die Enden. Der aktive Radio bekommt den
   * einzigen Tab-Stop; die uebrigen sind ueber tabindex=-1 erst per Pfeil
   * erreichbar (roving-tabindex-Muster).
   */
  onKeydown(event: KeyboardEvent, index: number): void {
    const items = this.items();
    if (items.length === 0) return;
    let next = index;
    if (event.key === 'ArrowRight' || event.key === 'ArrowDown') next = (index + 1) % items.length;
    else if (event.key === 'ArrowLeft' || event.key === 'ArrowUp') next = (index - 1 + items.length) % items.length;
    else if (event.key === 'Home') next = 0;
    else if (event.key === 'End') next = items.length - 1;
    else return;
    event.preventDefault();
    this.onSelect(items[next].id);
    const target = event.currentTarget as HTMLElement;
    const nextEl = target.parentElement?.children[next] as HTMLElement | undefined;
    nextEl?.focus();
  }
}
